import { forwardRef } from "react";
import { Lock, Sparkles, Check, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/lib/i18n/locale-context";

type LockedResultProps = {
  /** Bilder säljaren laddat upp – visar att analysen gäller deras prylar. */
  thumbs: string[];
  categoryLabel?: string | null;
  /** AI:ns rubrikförslag visas i sin helhet. Priset skickas aldrig hit. */
  title?: string | null;
  onRegister: () => void;
  onLogin: () => void;
};

/**
 * Resultatet ligger rakt ut i flödet – inga extra klick och ingen modal.
 * Rubriken visas, texten och priset är dolda tills man loggat in.
 */
export const LockedResultPanel = forwardRef<HTMLDivElement, LockedResultProps>(
  function LockedResultPanel({ thumbs, categoryLabel, title, onRegister, onLogin }, ref) {
    const { lang } = useLocale();
    const t = lang === "es" ? { uploaded: "Tu imagen subida", reviewed: "Revisado", one: "imagen", many: "imágenes", analysed: "analizadas", ready: "Tu anuncio está listo", intro: "Aquí tienes el título propuesto. La descripción y el precio aparecen cuando inicias sesión; crear una cuenta es gratis.", title: "Título", description: "Descripción", hiddenDescription: "La descripción aparece al iniciar sesión.", price: "Precio sugerido: inicia sesión para verlo", benefits: ["Es gratis y solo tarda diez segundos", "Todo tu trabajo se conserva y vuelves aquí directamente", "Publica el anuncio cuando estés satisfecho"], register: "Crear cuenta gratis y ver el resultado", login: "Ya tengo una cuenta", publish: "Nada se publica hasta que tú pulses Publicar." }
      : lang === "en" ? { uploaded: "Your uploaded image", reviewed: "Reviewed", one: "image", many: "images", analysed: "analysed", ready: "Your listing is ready", intro: "Here is the suggested title. The description and price appear when you sign in; creating an account is free.", title: "Title", description: "Description", hiddenDescription: "The description appears when you sign in.", price: "Price suggestion—sign in to view", benefits: ["It is free and takes ten seconds", "Everything you have done is saved and you return here", "Publish when you are happy with the listing"], register: "Create free account and view result", login: "I already have an account", publish: "Nothing is published until you select Publish." }
        : { uploaded: "Din uppladdade bild", reviewed: "Granskad", one: "bild", many: "bilder", analysed: "analyserade", ready: "Din annons är klar", intro: "Här är förslaget på rubrik. Beskrivningen och prisförslaget syns när du loggat in – det är gratis att skapa konto.", title: "Rubrik", description: "Beskrivning", hiddenDescription: "Beskrivningen visas när du loggat in.", price: "Prisförslag — syns när du loggat in", benefits: ["Det är gratis och tar tio sekunder", "Allt du gjort finns kvar – du kommer tillbaka hit direkt", "Publicera annonsen när du känner dig nöjd"], register: "Skapa gratiskonto — se resultatet", login: "Jag har redan konto", publish: "Inget publiceras förrän du själv trycker på publicera." };
    const [hero, ...others] = thumbs;
    const extra = Math.max(0, others.length - 4);

    return (
      <section
        ref={ref}
        className="mt-6 scroll-mt-24 overflow-hidden rounded-2xl border border-ocean-300/30 bg-ocean-800/40"
      >
        {hero && (
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-ocean-950">
             <img src={hero} alt={t.uploaded} className="h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ocean-950/90 to-transparent" />
            <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-ocean-950/80 px-2.5 py-1 text-xs font-medium text-ocean-100 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-ocean-300" />
               {t.reviewed}
              {categoryLabel ? ` · ${categoryLabel}` : ""}
            </span>
          </div>
        )}

        <div className="space-y-4 p-5">
          {others.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {others.slice(0, 4).map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="h-14 w-14 rounded-md object-cover ring-1 ring-white/10"
                />
              ))}
              {extra > 0 && (
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-md bg-white/10 text-sm font-medium text-ocean-100">
                  +{extra}
                </span>
              )}
            </div>
          )}

          <div>
            <p className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ocean-300">
              <ImageIcon className="h-3.5 w-3.5" />
               {thumbs.length} {thumbs.length === 1 ? t.one : t.many} {t.analysed}
            </p>
            <h2 className="mt-1 font-heading text-xl font-semibold text-white sm:text-2xl">
               {t.ready}
            </h2>
            <p className="mt-1 text-sm text-ocean-100/85">
               {t.intro}
            </p>
          </div>

          <div className="space-y-3 rounded-md border border-white/10 bg-white/5 p-4">
            <div>
               <p className="text-xs font-medium uppercase tracking-wide text-ocean-300">{t.title}</p>
              <p className="break-words font-heading text-lg font-semibold leading-snug text-white">
                 {(title ?? "").trim() || t.ready}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-ocean-300">
                 {t.description}
              </p>
              <div className="mt-1.5 space-y-1.5" aria-hidden>
                <div className="h-2.5 w-full rounded bg-white/10 blur-[3px]" />
                <div className="h-2.5 w-11/12 rounded bg-white/10 blur-[3px]" />
                <div className="h-2.5 w-4/5 rounded bg-white/10 blur-[3px]" />
              </div>
               <p className="sr-only">{t.hiddenDescription}</p>
            </div>
            <p className="inline-flex items-center gap-2 rounded-md bg-ocean-900/70 px-3 py-2 text-sm font-medium text-ocean-100">
              <Lock className="h-4 w-4 text-ocean-300" />
               {t.price}
            </p>
          </div>

          <ul className="space-y-1.5 text-sm text-ocean-100/85">
             {t.benefits.map((benefit) => (
               <li key={benefit} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-ocean-300" />
                 <span>{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="space-y-2">
            <Button type="button" onClick={onRegister} className="w-full">
              <Lock className="h-4 w-4" />
               {t.register}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onLogin}
              className="w-full border-white/30 bg-ocean-900/60 text-white hover:bg-ocean-800 hover:text-white"
            >
               {t.login}
            </Button>
            <p className="text-center text-xs text-ocean-100/70">
               {t.publish}
            </p>
          </div>
        </div>
      </section>
    );
  },
);
